import { IconChevron } from '@bvt-assets/icon/icon-chevron';
import { debounce, find } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import './DatexTelcoStepOneTelco.scss';
import moment from 'moment';
import { SelectOption } from '@bvt-shared/component/SelectOption';
import { YEAR_LIST } from '@bvt-features/datex/constant/YEAR_LIST';
import { useAreaSelection } from '@bvt-features/datex/hooks';
import { MobileData } from '@bvt-features/genset/DensityAndGrid/component/MobileData';

moment.updateLocale('en', {
  weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
});

/**
 * @copyright PT Bhumi Varta Technology
 * @author Renta<renta.yustika@bvarta.com>
 */

/**
 * @description For list time type
 * @typedef TTelcoTimeType
 * @type {Object}
 * @property {string|number} key
 * @property {string} label
 */

/**
 * @description For list time type
 * @typedef TCatchmentType
 * @type {Object}
 * @property {string|number} key
 * @property {string} label
 * @property {any} icon
 */

/**
 * @description For list select object
 * @typedef TSelectObject
 * @type {Object}
 * @property {number} value
 * @property {string} label
 */

/**
 * @description For data poi object
 * @typedef TTelcoDataObject
 * @type {object}
 * @property {number} province
 * @property {number} cityId
 * @property {string} city
 * @property {'daily'|'monthly'|'yearly'} dataType
 * @property {number} year
 * @property {number} month
 * @property {number} day
 */

/**
 * @param {object} props
 * @param {TTelcoDataObject} props.data
 * @param {(search:string)=>void} props.onSearchProvince
 * @param {TSelectObject} props.provinceList
 * @param {TCityTSelectObjectObject} props.cityList
 * @param {boolean} props.loadingProvince
 * @param {boolean} props.loadingCity
 * @param {(data:TTelcoDataObject)=>void} props.onChange
 * @param {object} props.unavailableDate
 * @param {(search:string,id_province:number)=>void} props.onSearchCity
 * @param {(e:boolean) => void} props.onValidOrInvalid
 */


export const DatexTelcoStepOneTelco = ({
  data,
  onSearchProvince,
  onSearchCity,
  onChange,
  onValidOrInvalid,
  provinceList,
  cityList,
  loadingCity,
  loadingProvince,
}) => {
  const debouncedSearchProvince = useCallback(debounce(onSearchProvince, 300), [
    onSearchProvince,
  ]);

  const debouncedSearchCity = useCallback(debounce(onSearchCity, 300), [
    onSearchCity,
  ]);

  useEffect(() => {
    const { province, city, dataType, year, month, day } = data;
    const validDate = year || month || day;
    const isValid = province && city && dataType && validDate;

    onValidOrInvalid?.(isValid);
  }, [data]);

  // Define the search functions that will be passed to the custom hook
  const searchFunctions = {
    province: debouncedSearchProvince,
    city: (value) => debouncedSearchCity(value, data?.province),
  };

  // Use the custom hook to get the render functions
  const { renderProvinceDropdown, renderCityDropdown, setInputSearch } =
    useAreaSelection(searchFunctions, 'DatexTelcoStepOneTelco__select--search');

  const mappingRevertTypeDataTelco = (type) => {
    switch (type) {
      case 'Hourly':
        return 'daily';
      case 'Daily':
        return 'monthly';
      case 'Monthly':
        return 'yearly';
      default:
        return type;
    }
  };

  return (
    <div className='DatexTelcoStepOneTelco__container'>
      <div className='DatexTelcoStepOneTelco__search'>
        <span>Select province and city/regency you want to explore.</span>
        <SelectOption
          disabled={loadingProvince}
          dropdownRender={renderProvinceDropdown}
          loading={loadingProvince}
          onChange={(e) => {
            onChange({ province: e, cityId: undefined, city: undefined });
            debouncedSearchCity('', e);
            setInputSearch((prev) => ({ ...prev, city: '' }));
          }}
          options={provinceList || []}
          placeholder='Select Province'
          suffixIcon={<IconChevron />}
          value={data?.province}
        />
        <SelectOption
          disabled={!data?.province || loadingCity}
          dropdownRender={renderCityDropdown}
          loading={loadingCity}
          onChange={(e) => {
            const selectedCity = find(cityList, (v) => v?.value === e);
            onChange({
              province: data?.province,
              city: e,
              cityId: selectedCity?.city_id,
            });
          }}
          options={cityList || []}
          placeholder='Select City/Regency'
          suffixIcon={<IconChevron />}
          value={data.city}
        />
      </div>
      <div className='DatexTelcoDataType'>
        <div className='DatexTelcoDataType_title'>Select Data Type</div>
        <MobileData
          day={data.day}
          disabled={!data.province || !data.city}
          isDatex
          month={data.month}
          onChange={(value) => {
            onChange?.({
              ...data,
              dataType: value.periodic,
              day: value.day || 0,
              month: value.month || 0,
              year: value.year || 0,
            });
          }}
          periodic={mappingRevertTypeDataTelco(data.dataType)}
          providedYear={YEAR_LIST.map((v) => v.value)}
          year={data.year}
        />
      </div>
    </div>
  );
};
